// shared-lib/room-split/seatEnds.js
//
// A chain end SEATED on a scanned wall counts as on the room's outline.
//
// A tap on a wall lands on its face, and the room outline is meant to run on
// that face — but on about 1% of scanned walls the outline sits more than
// `TOUCH_TOL_M` off it: a few cm into the wall's body, or a few cm in front
// of it (the #2759 review). The end then read as standing inside the room
// ("doesn't divide it") or outside it ("leaves the room"), and Extend to wall
// refused it too, the end already touching a wall: the user was stuck.
//
// So, for the cut only: an end touching another wall of the storey, whose
// outline lies along the chain within that wall's thickness, is carried
// along the chain onto the outline. The drawn wall is not moved — the carried
// end stays inside the scanned wall's body. An end touching no wall, or one
// whose outline is farther than a wall's thickness, keeps today's answer.
// Pure.

const { TOUCH_TOL_M, sameStorey } = require('./chain');
const { distanceToPolygonBoundary } = require('./geometry');
const { footprintRing, chainEnds } = require('./extend');
const { rayDistanceToPolygon } = require('../geometry/rectifyWalkways');
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');

const dist = (a, b) => Math.hypot(b.x - a.x, b.z - a.z);

/** Where one end lands on the outline, or null when it is not seated. */
function seatedAt(end, polygon, others) {
  if (distanceToPolygonBoundary(end.at, polygon) <= TOUCH_TOL_M) return null;
  const len = dist(end.from, end.at);
  if (!(len > 0)) return null;
  // the way the chain runs out at this end: on, if the end is inside the
  // room; back, if it stands outside
  const inside = isPointInsidePolygon(end.at, polygon);
  const sign = inside ? 1 : -1;
  const dir = {
    x: (sign * (end.at.x - end.from.x)) / len,
    z: (sign * (end.at.z - end.from.z)) / len,
  };
  for (const wall of others) {
    const ring = footprintRing(wall);
    const touches =
      isPointInsidePolygon(end.at, ring) ||
      distanceToPolygonBoundary(end.at, ring) <= TOUCH_TOL_M;
    if (!touches) continue;
    const t = rayDistanceToPolygon(end.at, dir, polygon);
    if (t == null || t > (wall.dimensions?.z ?? 0) + TOUCH_TOL_M) continue;
    return { x: end.at.x + dir.x * t, z: end.at.z + dir.z * t };
  }
  return null;
}

/**
 * `chain` with each end seated on a scanned wall carried onto `polygon`, or
 * the same chain when neither end is.
 *
 * @param {object} chain a `buildChain` result
 * @param {{x,z}[]} polygon the room's floor ring
 * @param {object[]} walls the structure's walls
 */
function seatChainEnds(chain, polygon, walls) {
  const inChain = new Set(chain.wallIds);
  const first = chain.stretches[0];
  const others = (walls || []).filter(
    (w) =>
      w?.id &&
      !inChain.has(w.id) &&
      w.center &&
      w.dimensions?.x > 0 &&
      sameStorey(
        w,
        (walls || []).find((x) => x?.id === first.wallId)
      )
  );
  const [startEnd, endEnd] = chainEnds(chain);
  const start = seatedAt(startEnd, polygon, others);
  const end = seatedAt(endEnd, polygon, others);
  if (!start && !end) return chain;
  const st = chain.stretches.map((s) => ({ ...s }));
  if (start) st[0].a = start;
  if (end) st[st.length - 1].b = end;
  const points = [...chain.points];
  if (start) points[0] = start;
  if (end) points[points.length - 1] = end;
  return { ...chain, stretches: st, points };
}

module.exports = { seatChainEnds };
